import type {
  HttpTransport,
  TransportRequestConfig,
} from '@mangovoice/api-client'
import type {
  ExtensionInstance,
  MessageGroup,
  PaginatedApiResult,
  PhoneNumber,
} from '@mangovoice/types'
import type { components } from '@mangovoice/types/generated/mango-api'

type Schedule = components['schemas']['Schedule']
type SoundClip = components['schemas']['SoundClip']

// Type aliases (not interfaces) so they satisfy the transport's
// Record<string, unknown> params without a cast.
export type SwitchboardListExtensionsParams = {
  pbx_id: number
  type?: string
  limit?: number
  offset?: number
  ordering?: string
  voicemail_enabled?: boolean
}

export type SwitchboardListPhoneNumbersParams = {
  pbx_id: number
  limit?: number
  offset?: number
  search?: string
  sms_eligible?: boolean
}

export type SwitchboardSchedule = Schedule

export type SwitchboardSoundClip = SoundClip

export function createSwitchboardResource(transport: HttpTransport) {
  return {
    get: <T>(
      url: string,
      params?: Record<string, unknown>,
      config?: TransportRequestConfig,
    ) => transport.get<T>(url, params, config),

    post: <T>(url: string, data?: unknown, config?: TransportRequestConfig) =>
      transport.post<T>(url, data, config),

    patch: <T>(url: string, data?: unknown, config?: TransportRequestConfig) =>
      transport.patch<T>(url, data, config),

    put: <T>(url: string, data?: unknown, config?: TransportRequestConfig) =>
      transport.put<T>(url, data, config),

    delete: <T>(url: string, config?: TransportRequestConfig) =>
      transport.delete<T>(url, undefined, config),

    listExtensions: (
      params: SwitchboardListExtensionsParams,
      config?: TransportRequestConfig,
    ) =>
      transport.get<PaginatedApiResult<ExtensionInstance>>(
        '/app/v1/extensions/',
        params,
        config,
      ),

    listMessageGroups: (params: {
      pbx_id: number
      limit?: number
      offset?: number
    }) =>
      transport.get<PaginatedApiResult<MessageGroup>>(
        '/app/v1/message-groups/',
        params,
      ),

    listPhoneNumbers: (params: SwitchboardListPhoneNumbersParams) =>
      transport.get<PaginatedApiResult<PhoneNumber>>(
        '/app/v2/phone-numbers/',
        params,
      ),

    listSchedules: (params: {
      pbx_id: number
      limit?: number
      offset?: number
    }) =>
      transport.get<PaginatedApiResult<SwitchboardSchedule>>(
        '/app/v1/schedules/',
        params,
      ),

    listSoundClips: (params: {
      pbx_id: number
      limit?: number
      offset?: number
    }) =>
      transport.get<PaginatedApiResult<SwitchboardSoundClip>>(
        '/app/v1/sound-clips/',
        params,
      ),
  }
}
